import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Calendar, MessageSquare, Target, Star, Users, AlertCircle } from 'lucide-react';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './notifications-widget.scss';

interface Notif {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  action_url?: string;
  created_at: string;
}

const iconFor = (type: string) => {
  switch (type) {
    case 'session_created':
    case 'session_updated':
    case 'session_cancelled':
    case 'session_reminder':
      return <Calendar size={16} />;
    case 'message_received':
      return <MessageSquare size={16} />;
    case 'goal_created':
    case 'goal_updated':
    case 'goal_completed':
      return <Target size={16} />;
    case 'evaluation_pending':
    case 'evaluation_completed':
      return <Star size={16} />;
    case 'relationship_created':
      return <Users size={16} />;
    case 'system_announcement':
      return <AlertCircle size={16} />;
    default:
      return <Bell size={16} />;
  }
};

const timeAgo = (sql: string): string => {
  const d = new Date(String(sql).replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
};

interface Props { limit?: number; animateDelay?: number; }

const NotificationsWidget: React.FC<Props> = ({ limit = 5, animateDelay = 0.35 }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleBase = user?.role_name === 'mentor' ? '/mentor' : user?.role_name === 'mentee' ? '/mentee' : '/admin';
  const [items, setItems] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiService.notifications.getAll({ limit })
      .then(res => { if (alive) setItems((res.data?.data || []).slice(0, limit)); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [limit]);

  const unread = items.filter(n => !n.is_read).length;

  const open = async (n: Notif) => {
    if (!n.is_read) {
      try { await apiService.notifications.markAsRead(n.id); } catch { /* noop */ }
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
    }
    if (n.action_url && n.action_url.startsWith('/')) navigate(n.action_url);
    else navigate(`${roleBase}/notifications`);
  };

  return (
    <Card animate animateDelay={animateDelay}>
      <CardHeader action={<button className="nw-all" onClick={() => navigate(`${roleBase}/notifications`)}>Ver todas →</button>}>
        <CardTitle>
          Notificaciones{unread > 0 && <span className="nw-badge">{unread}</span>}
        </CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <p className="nw-empty">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="nw-empty">No tienes notificaciones.</p>
        ) : (
          <div className="nw-list">
            {items.map(n => (
              <button key={n.id} className={`nw-item ${n.is_read ? '' : 'is-unread'}`} onClick={() => open(n)}>
                <span className="nw-item__icon">{iconFor(n.type)}</span>
                <span className="nw-item__body">
                  <span className="nw-item__title">{n.title}</span>
                  {n.message && <span className="nw-item__msg">{n.message}</span>}
                  <span className="nw-item__time">{timeAgo(n.created_at)}</span>
                </span>
                {!n.is_read && <span className="nw-item__dot" aria-hidden />}
              </button>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default NotificationsWidget;
